import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Loader2, ShieldCheck, Smartphone, Building2, FileText } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';

type PaymentMethod = 'chapa' | 'telebirr' | 'bank_transfer' | 'manual';

const PAYMENT_OPTIONS: { value: PaymentMethod; label: string; desc: string; icon: any }[] = [
  { value: 'chapa', label: 'Chapa', desc: 'Cards, mobile money, bank — instant', icon: Smartphone },
  { value: 'telebirr', label: 'Telebirr', desc: 'Pay from your Telebirr wallet', icon: Smartphone },
  { value: 'bank_transfer', label: 'Bank Transfer', desc: 'CBE / Awash / Dashen — 1 business day', icon: Building2 },
  { value: 'manual', label: 'Manual / Invoice', desc: 'Our team will arrange offline', icon: FileText },
];

interface Pkg {
  id: string;
  name: string;
  tier_label: string | null;
  audience: string;
  price_etb: number;
  price_currency: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pkg: Pkg | null;
}

export function PackageInquiryDialog({ open, onOpenChange, pkg }: Props) {
  const { user } = useAuth();
  const [fullName, setFullName] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('chapa');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && user) {
      setFullName((user.user_metadata?.full_name as string) || '');
      setEmail(user.email || '');
      setPhone((user.user_metadata?.phone as string) || '');
    }
    if (!open) {
      setMessage('');
      setPaymentMethod('chapa');
    }
  }, [open, user]);

  const submit = async () => {
    if (!fullName.trim() || !email.trim() || !phone.trim()) {
      toast.error('Please fill name, email and phone');
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error('Enter a valid email');
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from('package_inquiries' as any).insert({
      package_id: pkg?.id || null,
      user_id: user?.id || null,
      full_name: fullName.trim(),
      company: company.trim() || null,
      phone: phone.trim(),
      email: email.trim().toLowerCase(),
      audience: pkg?.audience || null,
      message: message.trim() || null,
      preferred_payment_method: paymentMethod,
    });
    setSubmitting(false);
    if (error) { toast.error(error.message); return; }
    toast.success('Thanks! Our team will contact you within 1 business day.');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Inquire about {pkg?.tier_label || pkg?.name}</DialogTitle>
          <DialogDescription>
            {pkg && (
              <>{pkg.price_currency} {Number(pkg.price_etb).toLocaleString()} — our advertising team will reach out with details.</>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Full name *</Label>
              <Input value={fullName} onChange={(e) => setFullName(e.target.value)} />
            </div>
            <div>
              <Label>Company / Agency</Label>
              <Input value={company} onChange={(e) => setCompany(e.target.value)} />
            </div>
            <div>
              <Label>Phone *</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+251…" />
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
          </div>
          <div>
            <Label>Message (optional)</Label>
            <Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)}
              placeholder="Tell us about your business and what you'd like to achieve…" />
          </div>
          <div>
            <Label className="mb-2 block">Preferred payment method *</Label>
            <RadioGroup
              value={paymentMethod}
              onValueChange={(v) => setPaymentMethod(v as PaymentMethod)}
              className="grid grid-cols-1 sm:grid-cols-2 gap-2"
            >
              {PAYMENT_OPTIONS.map((opt) => {
                const Icon = opt.icon;
                const selected = paymentMethod === opt.value;
                return (
                  <label
                    key={opt.value}
                    htmlFor={`pm-${opt.value}`}
                    className={`flex items-start gap-2 rounded-lg border p-3 cursor-pointer transition-colors ${
                      selected ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                    }`}
                  >
                    <RadioGroupItem value={opt.value} id={`pm-${opt.value}`} className="mt-0.5" />
                    <Icon className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium leading-none">{opt.label}</p>
                      <p className="text-[11px] text-muted-foreground mt-1">{opt.desc}</p>
                    </div>
                  </label>
                );
              })}
            </RadioGroup>
          </div>
          <Alert>
            <ShieldCheck className="h-4 w-4" />
            <AlertDescription className="text-xs">
              We only use these details to contact you about advertising. Payment instructions for your chosen method
              will be shared after approval.
            </AlertDescription>
          </Alert>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting}>
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Send inquiry
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
